import * as Errors from '../Shared/Constants/Errors';
import clamp from '../Shared/Util/clamp';
import findIndices from '../Shared/Util/findIndices';

import Alignment from './Constants/Alignment';
import FocusableNode from './FocusableNode';

const clampBasis = clamp(0, 100);

/**
 * A static class containing various utility methods dealing with the
 * calculation of node bases, and the handover of focus based on said
 * values, when transferring focus between parallel containers within
 * a perpendicular "justified" parent container.
 */

class JustifiedFocusWorker {
    /**
     * Given an array of focusable nodes (a container's children), will calculate a `basis` value
     * for each node, which can be thought of as a width or height (depending on the container
     * orientation) in virtual focus space.
     *
     * By default, basis will be calculated by evenly dividing `100` (100%) by the number of
     * children in a list container or columns in a grid container. However, if an explicit basis
     * override has been provided to a node as a prop/option, then it will be used instead, and any
     * node without an override will fall back to a basis value determined by evenly dividing the
     * left-over space between all other nodes without overrides.
     */

    public static defineBases(parent: FocusableNode): void {
        const {columns, children, isGrid, finalGridRowAlign} = parent;
        const childCount = isGrid ? parent.columns : children.length;
        const overridenBases = findIndices(children, node => !isNaN(node.basisOverride));
        const hasBasisOverrides = overridenBases.values.length > 0;

        if (hasBasisOverrides && isGrid) {
            throw new Error(Errors.ILLEGAL_BASIS_OVERRIDE(parent.id));
        }

        const sumOfAllOverridenBases = hasBasisOverrides
            ? clampBasis(
                  overridenBases.values.reduce((sum, node) => {
                      sum = sum + node.basisOverride;

                      return sum;
                  }, 0)
              )
            : 0;

        const nonOverridenBasesCount = childCount - overridenBases.values.length;

        const generalBasis = hasBasisOverrides
            ? (100 - sumOfAllOverridenBases) / nonOverridenBasesCount
            : 100 / childCount;

        const lastRowIndex = Math.ceil(children.length / columns) - 1;
        const totalNodesOnLastRow = children.length % columns;

        const lastRowBasis =
            finalGridRowAlign === Alignment.JUSTIFY && totalNodesOnLastRow > 0 && totalNodesOnLastRow < columns
                ? 100 / totalNodesOnLastRow
                : generalBasis;

        children.forEach((node, i) => {
            const currentRowIndex = JustifiedFocusWorker.getGridNodeRowIndex(node);

            if (node.anchorsOverride.length > 0) {
                if (isGrid) throw new Error(Errors.ILLEGAL_ANCHORS_OVERRIDE(parent.id));

                node.anchors = node.anchorsOverride;
            }

            if (currentRowIndex === lastRowIndex && lastRowBasis !== generalBasis) {
                node.basis = lastRowBasis;

                node.anchors = JustifiedFocusWorker.getAnchorsForItemOnFinalJustifiedRow(i, columns, lastRowBasis);
            } else if (hasBasisOverrides && overridenBases[i]) {
                node.basis = clampBasis(node.basisOverride);
            } else {
                node.basis = generalBasis;
            }
        });
    }

    /**
     * Returns the index of a node, and its closest anchor index in a neighbouring
     * justified container, closest to a node in a "sender" container, in virtual
     * focus space.
     *
     * @param senderContainer The container sending focus
     * @param receiverContainer The container receiving focus
     * @param childIndices A path to the original sender node child,
     *  relative ot the sender container.
     * @param currentFocusedAnchorIndex The index of the focused anchor for
     *  the current focused node.
     * @param directionOfTravel A number indicating the direction that focus
     *  is moving.
     */

    public static getClosestChildIndexAndAnchorIndexOfContainer(
        senderContainer: FocusableNode,
        receiverContainer: FocusableNode,
        childIndices: number[],
        currentFocusedAnchorIndex: number,
        directionOfTravel: -1 | 0 | 1
    ): [number, number] {
        const {columns, children, isGrid, finalGridRowAlign} = receiverContainer;
        const senderNode = JustifiedFocusWorker.getChildAtPath(senderContainer, [...childIndices]);
        const totalGridRows = Math.ceil(children.length / columns);
        const firstIndexOfLastRow = (totalGridRows - 1) * columns;
        const isReceiverGridAndRetreating = isGrid && directionOfTravel < 0;
        const isReceiverGridAndAdvancing = isGrid && directionOfTravel > 0;
        const incompleteColumns = children.length % columns;

        // Start iteration from the first node of the final row, if a grid
        // and retreating, else start from 0

        const startIndex = isReceiverGridAndRetreating ? firstIndexOfLastRow : 0;

        let receiverStartPosition = 0;
        let endIndex = children.length - 1;

        if (isReceiverGridAndRetreating && incompleteColumns > 0 && finalGridRowAlign === Alignment.RIGHT) {
            // If the final grid row is incomplete, and right-aligned, front-load
            // `receiverStartPosition` to account for empty columns to the left

            receiverStartPosition = incompleteColumns * children[startIndex].basis;
        }

        // If grid and beyond first row (while advancing), break

        if (isReceiverGridAndAdvancing) {
            endIndex = startIndex + columns - 1;
        }

        return JustifiedFocusWorker.findClosestNodeIndexAndAnchorIndex(
            senderNode,
            currentFocusedAnchorIndex,
            children,
            startIndex,
            endIndex,
            receiverStartPosition
        );
    }

    /**
     * Returns the start position (in basis percentage units), of a particular node
     * by summing the basis of all previous siblings.
     */

    public static getNodeStartPosition(node: FocusableNode): number {
        const startingRowIndex = JustifiedFocusWorker.getGridNodeRowIndex(node);

        let position = 0;

        while (
            node.previousSibling &&
            JustifiedFocusWorker.getGridNodeRowIndex(node.previousSibling) === startingRowIndex
        ) {
            position += node.previousSibling.basis;

            node = node.previousSibling;
        }

        return position;
    }

    /**
     * Traverses a set of children to find the index of the one closest to the provided
     * "sender node", and the index of the closest anchor within that node.
     *
     * @param senderNode The currently focused node.
     * @param senderNodeFocusedAnchorIndex The index of the focused anchor for the sender node.
     * @param children An array of possible nodes to receive focus (in another container, or grid row).
     * @param startIndex The index of the child at which to start iteration.
     * @param endIndex The index of the child at which to end iteration.
     * @param receiverStartPositionOffset A value (in basis percentage units) to offset the
     *  start position of the first possible receiver node (e.g. in order to account for a
     *  right-aligned grid row).
     */

    public static findClosestNodeIndexAndAnchorIndex(
        senderNode: FocusableNode,
        senderNodeFocusedAnchorIndex: number,
        children: FocusableNode[],
        startIndex: number,
        endIndex: number,
        receiverStartPositionOffset: number
    ): [number, number] {
        const senderStartPosition = JustifiedFocusWorker.getNodeStartPosition(senderNode);
        const senderFocusedAnchor = senderNode.anchors[senderNodeFocusedAnchorIndex];
        const senderDetachPoint = senderStartPosition + senderNode.basis * (senderFocusedAnchor / 100);

        let closestIndex = -1;
        let closestDistance = Infinity;
        let closestAnchorIndex = -1;
        let receiverStartPosition = receiverStartPositionOffset;

        receiverNodeLoop: for (let nodeIndex = startIndex; nodeIndex <= endIndex; nodeIndex++) {
            const receiverNode = children[nodeIndex];
            const receiverBasis = receiverNode.basis;

            if (receiverNode.isFocusDisabled) {
                receiverStartPosition += receiverBasis;

                continue receiverNodeLoop;
            }

            for (let anchorIndex = 0; anchorIndex < receiverNode.anchors.length; anchorIndex++) {
                const possibleReceiverAnchor = receiverNode.anchors[anchorIndex];
                const receiverAttachPoint = receiverStartPosition + receiverBasis * (possibleReceiverAnchor / 100);
                const currentDistance = Math.abs(receiverAttachPoint - senderDetachPoint);

                if (currentDistance < closestDistance) {
                    // A closer node was found, update data

                    closestIndex = nodeIndex;
                    closestDistance = currentDistance;
                    closestAnchorIndex = anchorIndex;
                } else {
                    // Closest node found, no need for further iteration.

                    break receiverNodeLoop;
                }
            }

            receiverStartPosition += receiverBasis;
        }

        return [closestIndex, closestAnchorIndex];
    }

    /**
     * Given an index for a node on the final row justified grid row, returns the appropriate
     * column-aligned anchors for that node, based on the number of grid columns and the basis
     * for that node.
     *
     * @param nodeIndex The index of the node to return anchors for.
     * @param columns The number of columns in the grid.
     * @param basis The pre-calculated basis for the justified grid item.
     * @return An array of one or more anchors for the node.
     */

    private static getAnchorsForItemOnFinalJustifiedRow(nodeIndex: number, columns: number, basis: number): number[] {
        const nodeIndexInRow = nodeIndex % columns;
        const columnBasis = 100 / columns;
        const columnMidPointOffset = columnBasis / 2;

        const nodeStartPoint = nodeIndexInRow * basis;
        const nodeEndPoint = nodeStartPoint + basis;

        return [...Array(columns)].reduce((anchors, _, columnIndex) => {
            const absoluteAnchor = columnIndex * columnBasis + columnMidPointOffset;

            if (absoluteAnchor >= nodeStartPoint && absoluteAnchor <= nodeEndPoint) {
                const relativeAnchor = ((absoluteAnchor - nodeStartPoint) / basis) * 100;

                anchors.push(relativeAnchor);
            }

            return anchors;
        }, []);
    }

    /**
     * Returns the child of the provided node, at the end of a provided path of
     * indices.
     */

    private static getChildAtPath(node: FocusableNode, path: number[]): FocusableNode {
        while (path.length) node = node.children[path.pop()!];

        return node;
    }

    /**
     * Returns the row index for a given grid item.
     */

    private static getGridNodeRowIndex(node: FocusableNode): number {
        const isGrid = node.parent!.isGrid;

        return isGrid ? Math.floor(node.index / node.parent!.columns) : 0;
    }
}

export default JustifiedFocusWorker;
